import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { TextSection } from './text-panel';
import { BackgroundSection } from './background-panel';
import { LayersSection } from './layers-panel';
import { ElementsSection } from './elements-panel';
import { UploadSection } from './upload-panel';
import { ImageEffectsPanel } from '../toolbar/image-effects-panel';
import { ImageMaskPanel } from '../toolbar/image-mask-panel';
import { TextEffectsPanel } from '../toolbar/text-effects-panel';

export const DEFAULT_SECTIONS = [
  TextSection,
  ElementsSection,
  UploadSection,
  BackgroundSection,
  LayersSection,
];

const SidePanel = observer(({ store, sections, defaultSection }) => {
  const sectionsList = sections || DEFAULT_SECTIONS;
  const [activeSection, setActiveSection] = useState(null);

  // Sync with store.openedSidePanel
  useEffect(() => {
    if (store.openedSidePanel) {
      const section = sectionsList.find((s) => s.name === store.openedSidePanel);
      if (section) {
        setActiveSection(section.name);
      } else if (store.openedSidePanel === '') {
        setActiveSection(null);
      }
    }
  }, [store.openedSidePanel]);

  // Set initial active section
  useEffect(() => {
    if (defaultSection) {
      const section = sectionsList.find((s) => s.name === defaultSection);
      if (section) {
        setActiveSection(section.name);
      }
    } else if (!activeSection && sectionsList.length > 0) {
      setActiveSection(sectionsList[0].name);
    }
  }, []);

  const handleTabClick = (sectionName) => {
    if (activeSection === sectionName) {
      // Toggle off
      setActiveSection(null);
      store.openSidePanel('');
    } else {
      setActiveSection(sectionName);
      store.openSidePanel(sectionName);
    }
  };

  const activeSectionObj = sectionsList.find((s) => s.name === activeSection);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        borderRight: '1px solid #e0e0e0',
        position: 'relative',
      }}
    >
      {/* Tab strip */}
      <Box
        sx={{
          width: 72,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 1,
          gap: 0.5,
          borderRight: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          overflowY: 'auto',
          flexShrink: 0,
        }}
      >
        {sectionsList.map((section) => {
          const isActive = activeSection === section.name;
          const TabComponent = section.Tab;
          if (section.visibleInList === false) return null;
          return (
            <Box key={section.name} sx={{ width: '100%', px: 0.5 }}>
              <TabComponent
                onClick={() => handleTabClick(section.name)}
                active={isActive}
              />
            </Box>
          );
        })}
      </Box>

      {/* Toolbar panel overlay (Effects / Mask) */}
      {store._toolbarPanel ? (
        store._toolbarPanel.type === 'effects' ? (
          (() => {
            const el = store.getElementById(store._toolbarPanel.elementId);
            if (el && el.type === 'text') return <TextEffectsPanel store={store} />;
            return <ImageEffectsPanel store={store} />;
          })()
        ) : store._toolbarPanel.type === 'mask' ? (
          <ImageMaskPanel store={store} />
        ) : null
      ) : (
        /* Normal panel content */
        activeSectionObj && (
          <Box
            sx={{
              width: 325,
              overflowY: 'auto',
              overflowX: 'hidden',
              backgroundColor: '#ffffff',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <activeSectionObj.Panel store={store} />
          </Box>
        )
      )}

      {/* Close / collapse button — rounded pill on right edge */}
      {activeSectionObj && (
        <Box
          onClick={() => { setActiveSection(null); store.openSidePanel(''); }}
          sx={{
            position: 'absolute',
            right: -16,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 16,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderLeft: 'none',
            borderRadius: '0 10px 10px 0',
            boxShadow: '2px 0 6px rgba(0,0,0,0.06)',
            zIndex: 10,
            '&:hover': { backgroundColor: '#f5f5f5' },
            transition: 'background-color 0.15s',
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 14, color: '#aaa' }} />
        </Box>
      )}
    </Box>
  );
});

SidePanel.displayName = 'SidePanel';
export default SidePanel;
export { SidePanel };

// Re-export sections for polotno-compatible import path:
// import { TextSection } from 'labellife-design-tool/side-panel/side-panel'
export { TextSection } from './text-panel';
export { BackgroundSection } from './background-panel';
export { LayersSection } from './layers-panel';
export { ElementsSection } from './elements-panel';
export { UploadSection } from './upload-panel';
export { SectionTab } from './tab-button';
